import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LayoutDashboard, GraduationCap, Lock, ArrowUpRight, Megaphone, KeyRound, ShieldCheck, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import BlogManager from "@/components/BlogManager";
import GalleryManager from "@/components/GalleryManager";
import ContentManager from "@/components/ContentManager";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTALS = [
  {
    icon: LayoutDashboard,
    title: "Pharmacy Dashboard",
    desc: "Staff login for the pharmacy operations dashboard — bookings, compounding and daily workflow.",
    url: "https://compound-hub-7.emergent.host/?utm_source=share",
    testid: "staff-dashboard-link",
  },
  {
    icon: GraduationCap,
    title: "Compounding & DermaGen Training",
    desc: "Training portal for compounding staff and DermaGen stockists.",
    url: "https://dermagen-learn.emergent.host/?utm_source=share",
    testid: "staff-training-link",
  },
];

function BannerManager({ pin }) {
  const [cfg, setCfg] = useState({ enabled: false, message: "", cta_text: "Book now", cta_url: "#book" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/banner`).then((res) => {
      setCfg({
        enabled: !!res.data.enabled,
        message: res.data.message || "",
        cta_text: res.data.cta_text || "Book now",
        cta_url: res.data.cta_url || "#book",
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/banner`, { ...cfg, pin });
      toast.success(cfg.enabled ? "Banner is live at the top of the site" : "Banner turned off");
    } catch {
      toast.error("Could not save — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 border border-white/15 bg-white/5 p-8" data-testid="banner-manager">
      <div className="flex items-start gap-5">
        <div className="border border-white/20 p-3 text-brand-green">
          <Megaphone className="h-6 w-6" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold">Promo banner manager</h3>
          <p className="mt-1 text-sm leading-relaxed text-white/60">
            Turn the top-of-site banner on or off for flu season, vaccination drives or any promotion. Changes go live immediately.
          </p>
        </div>
      </div>

      <div className="mt-6 max-w-2xl space-y-4" data-testid="banner-controls">
        <label className="flex items-center gap-3 text-sm font-medium" data-testid="banner-toggle-label">
          <Switch
            checked={cfg.enabled}
            onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))}
            data-testid="banner-enabled-switch"
          />
          {cfg.enabled ? "Banner ON — visible at the top of the site" : "Banner OFF"}
        </label>
        <Input
          value={cfg.message}
          onChange={(e) => setCfg((c) => ({ ...c, message: e.target.value }))}
          placeholder="Banner message, e.g. Flu season is here — book your flu vaccination today"
          data-testid="banner-message-input"
          className="rounded-none border-white/20 bg-transparent text-white placeholder:text-white/40"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            value={cfg.cta_text}
            onChange={(e) => setCfg((c) => ({ ...c, cta_text: e.target.value }))}
            placeholder="Button text (e.g. Book now)"
            data-testid="banner-cta-input"
            className="rounded-none border-white/20 bg-transparent text-white placeholder:text-white/40"
          />
          <Input
            value={cfg.cta_url}
            onChange={(e) => setCfg((c) => ({ ...c, cta_url: e.target.value }))}
            placeholder="Button link (e.g. #book)"
            data-testid="banner-url-input"
            className="rounded-none border-white/20 bg-transparent text-white placeholder:text-white/40"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving || !cfg.message.trim()}
          data-testid="banner-save-btn"
          className="rounded-none bg-brand-green px-6 font-heading font-semibold text-white hover:bg-brand-greendark disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save banner"}
        </Button>
        <p className="text-xs text-white/40">Tip: use #book to jump visitors straight to the booking form, or paste any full https link.</p>
      </div>
    </div>
  );
}

export default function StaffPortal() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem("staff_pin");
    if (saved) {
      setPin(saved);
      setUnlocked(true);
    }
    const handler = () => setOpen(true);
    window.addEventListener("open-staff", handler);
    return () => window.removeEventListener("open-staff", handler);
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/banner/verify`, { pin });
      sessionStorage.setItem("staff_pin", pin);
      setUnlocked(true);
    } catch {
      toast.error("Incorrect passcode");
    }
  };

  const lock = () => {
    sessionStorage.removeItem("staff_pin");
    setPin("");
    setUnlocked(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="staff-dialog"
        className={`border-white/15 bg-[#141414] text-white ${
          unlocked ? "max-h-[88vh] max-w-4xl overflow-y-auto" : "max-w-md"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-white/50" data-testid="staff-eyebrow">
              <Lock className="h-3.5 w-3.5" /> Staff Only
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight" data-testid="staff-heading">
              Team portals & site tools
            </h2>
          </div>
          {unlocked && (
            <button
              onClick={lock}
              data-testid="staff-lock-btn"
              className="flex items-center gap-2 text-sm font-medium text-white/50 transition-colors duration-200 hover:text-white"
            >
              <LogOut className="h-4 w-4" /> Lock & close
            </button>
          )}
        </div>

        {!unlocked ? (
          <div className="mt-6" data-testid="staff-gate">
            <div className="flex items-start gap-5">
              <div className="border border-white/20 p-3 text-brand-green">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold">Restricted area</h3>
                <p className="mt-1 text-sm leading-relaxed text-white/60">
                  This area is for pharmacy staff only. Enter the staff passcode to access the dashboard,
                  training portal and website tools.
                </p>
              </div>
            </div>
            <form onSubmit={unlock} className="mt-6 flex items-center gap-3" data-testid="staff-gate-form">
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Staff passcode"
                data-testid="staff-pin-input"
                className="rounded-none border-white/20 bg-transparent text-white placeholder:text-white/40"
              />
              <Button type="submit" data-testid="staff-unlock-btn" className="rounded-none bg-brand-green px-5 font-heading font-semibold text-white hover:bg-brand-greendark">
                <KeyRound className="mr-2 h-4 w-4" /> Unlock
              </Button>
            </form>
          </div>
        ) : (
          <div className="mt-6" data-testid="staff-unlocked-area">
            <div className="grid gap-4 md:grid-cols-2" data-testid="staff-portals">
              {PORTALS.map((p) => (
                <a
                  key={p.title}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={p.testid}
                  className="group flex items-start justify-between gap-6 rounded-none border border-white/15 bg-white/5 p-6 backdrop-blur transition-[background-color,border-color] duration-200 hover:border-white/40 hover:bg-white/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="border border-white/20 p-3 text-brand-green">
                      <p.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-bold">{p.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-white/60">{p.desc}</p>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-white/40 transition-[transform,color] duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-green" />
                </a>
              ))}
            </div>

            <BannerManager pin={pin} />
            <BlogManager pin={pin} />
            <GalleryManager pin={pin} />
            <ContentManager pin={pin} kind="services" />
            <ContentManager pin={pin} kind="learn" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
